/*
 * PROD-7828 W4 — Bank payouts: query params and request bodies, pinned to teacher_payout_ops_enums
 * (request_fields_read/required) by request_fields.test.js. Every month is 'YYYY-MM'; amounts go out as
 * rupee strings so no float ever reaches the server.
 */
import { trim_reason } from '../needs_attention/params';

export const PAYOUT_PAGE_SIZE = 50;

// The generate kinds (teacher_payout_ops_enums file_kinds).
export const KIND_R1 = 'R1';
export const KIND_REGENERATE = 'REGENERATE';
export const KIND_NEW_ROUND = 'NEW_ROUND';

export const ACTION_SENT = 'COUNT_AS_SENT';
export const ACTION_FAILED = 'COUNT_AS_FAILED';
export const ACTION_IGNORE = 'IGNORE';

const s = (v) => (typeof v === 'string' ? v.trim() : '');

// teacher_uuids '' = every teacher of the month (the builder keeps the key; the query drops it).
export const eligibility_params = (pay_month, teacher_uuids = []) => ({
  pay_month,
  teacher_uuids: (teacher_uuids || []).join(',')
});

/*
 * choice: {kind, file_uuid}. R1 sends neither option, REGENERATE names the file it replaces, NEW_ROUND
 * sets new_round (the server refuses both at once). confirm_pay_month is what Ops typed, untouched but trimmed.
 */
export const generate_body = ({ pay_month, confirm_pay_month, client_request_id, choice }) => {
  const c = choice || {};
  return {
    pay_month,
    confirm_pay_month: s(confirm_pay_month),
    client_request_id,
    replaces_file_uuid: c.kind === KIND_REGENERATE ? c.file_uuid || '' : '',
    new_round: c.kind === KIND_NEW_ROUND,
    teacher_uuids: []
  };
};

export const files_params = (pay_month) => ({ pay_month });

export const file_csv_params = (file_uuid) => ({ file_uuid });

// multipart fields of POST /payouts/responses/upload (additional_payments' to_form_data builds the body).
export const upload_fields = (pay_month, client_request_id, file) => ({
  pay_month,
  client_request_id,
  file
});

// What makes two uploads "the same attempt": the month and the file, never the bytes themselves.
export const upload_signature = (pay_month, file) => ({
  pay_month,
  name: (file && file.name) || '',
  size: (file && file.size) || 0,
  last_modified: (file && file.lastModified) || 0
});

export const preview_params = (response_uuid) => ({ response_uuid });

export const apply_body = (response_uuid) => ({ response_uuid });

export const held_params = (after_id, limit = PAYOUT_PAGE_SIZE) => ({
  after_id: after_id || 0,
  limit
});

// bank_reference and sent_on only count for COUNT_AS_SENT; '' lets the server use the line's own.
export const resolve_body = ({
  line_id,
  action,
  reason,
  bank_reference,
  sent_on,
  payout_item_uuid
}) => {
  const sent = action === ACTION_SENT;
  return {
    line_id,
    action,
    reason: trim_reason(reason),
    bank_reference: sent ? s(bank_reference) : '',
    sent_on: sent ? s(sent_on) : '',
    payout_item_uuid: action === ACTION_IGNORE ? '' : s(payout_item_uuid)
  };
};

export const retry_body = (payout_item_uuid, reason) => ({
  payout_item_uuid,
  reason: trim_reason(reason)
});

export const paid_outside_body = ({
  teacher_uuid,
  pay_month,
  amount,
  paid_on,
  reference,
  reason,
  client_request_id,
  additional_payment_uuid
}) => ({
  teacher_uuid,
  pay_month,
  amount: s(amount),
  paid_on: s(paid_on),
  reference: s(reference),
  reason: trim_reason(reason),
  client_request_id,
  additional_payment_uuid: s(additional_payment_uuid)
});

export const teacher_month_params = (pay_month, teacher_uuid) => ({ pay_month, teacher_uuid });

export const deleted_params = (after_teacher_id, limit = PAYOUT_PAGE_SIZE) => ({
  after_teacher_id: after_teacher_id || 0,
  limit
});

// D10: one teacher's deleted state (the profile banner).
export const deleted_teacher_params = (teacher_uuid) => ({ teacher_uuid });

export const claims_params = (status, after_id, limit = PAYOUT_PAGE_SIZE) => ({
  status: status || 'OPEN',
  after_id: after_id || 0,
  limit
});

export const claim_resolve_body = (claim_uuid, note) => ({ claim_uuid, note: trim_reason(note) });

export const holidays_params = (year) => ({ year: year ? String(year) : '' });

export const holiday_add_body = (date, name) => ({ date: s(date), name: s(name) });

export const holiday_remove_body = (date) => ({ date });
